/**
 * DIE KLADDE (NT-TUTORIAL-KLADDE-01) — Dashboard-Tab unter /kladde (seit NT-LAYOUT-01 keine
 * eigenständige Seite mehr, sondern im Content-Bereich der App-Shell).
 * Zwei Ansichten (CHECKLISTE / SPICKZETTEL) über einen leichten In-Page-Umschalter (kein
 * wiederverwendbares Segmented-Control-Muster im Repo gefunden, siehe Schritt-0-Bericht) —
 * kein neuer Tab-Bar-Mechanismus, State-Variable analog zum bestehenden `jobsView`-Muster.
 */

import { fetchKladdeState, dismissKladdeHint, type KladdeState } from '../services/kladde.js';
import { renderStagesWithHints, renderSpickzettel } from '../utils/kladde-render.js';

type KladdeView = 'checkliste' | 'spickzettel';
let currentView: KladdeView = 'checkliste';
let kladdeState: KladdeState | null = null;

// .kladde-stage/-milestone/-hint-card/-spickzettel-entry-Klassen leben bewusst zentral in
// dashboard.css (global geladen, nie erst zur Laufzeit injiziert) — nicht hier erneut definieren.
// Sonst reproduziert sich exakt die in NT-UI-DEALER-LIEFERN-01/markt.ts dokumentierte Falle:
// ungestyltes Markup für Spieler, die vor dieser Seite nie den Jobs-Tab (Home-Panel) besucht haben.
function addStyles(): void {
  const existing = document.getElementById('kladde-styles');
  const el = existing ?? document.createElement('style');
  el.id = 'kladde-styles';
  el.textContent = `
    .kladde-page { max-width: 700px; margin: 0 auto; }
    .kladde-title { font-size: var(--font-size-xl); color: var(--text-primary); margin: 0 0 var(--space-3); }
    .kladde-view-toggle { display: flex; gap: var(--space-2); margin-bottom: var(--space-3); }
    .kladde-view-toggle button { flex: 1 1 0; background: var(--bg-surface); border: 1px solid var(--border); color: var(--text-muted); font-family: var(--font-mono); font-size: var(--font-size-xs); padding: var(--space-2); border-radius: var(--radius-btn); cursor: pointer; }
    .kladde-view-toggle button.is-active { color: var(--accent-gold); border-color: var(--accent-gold); }
    .kladde-loading { color: var(--text-muted); font-size: var(--font-size-sm); }
  `;
  document.head.appendChild(el);
}

function renderContent(): string {
  if (!kladdeState) return '<p class="kladde-loading">Lade...</p>';
  if (currentView === 'spickzettel') return renderSpickzettel(kladdeState.hints);

  const visibleStages = kladdeState.stages.filter((stage) => stage.visible);
  if (visibleStages.length === 0) return '<p class="kladde-loading">Noch nichts freigeschaltet.</p>';
  return renderStagesWithHints(visibleStages, kladdeState.hints);
}

function bindContent(): void {
  document.querySelectorAll<HTMLButtonElement>('[data-kladde-hint-dismiss]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const key = btn.dataset.kladdeHintDismiss;
      if (!key) return;
      void dismissKladdeHint(key).then(refresh).catch(() => { /* stumm ignorieren */ });
    });
  });
}

async function refresh(): Promise<void> {
  const contentEl = document.getElementById('kladde-page-content');
  if (!contentEl) return;
  try {
    kladdeState = await fetchKladdeState();
  } catch {
    contentEl.innerHTML = '<p class="kladde-loading">Konnte nicht geladen werden.</p>';
    return;
  }
  contentEl.innerHTML = renderContent();
  bindContent();
}

function setView(view: KladdeView): void {
  if (view === currentView) return;
  currentView = view;
  document.querySelectorAll<HTMLButtonElement>('[data-kladde-view]').forEach((btn) => {
    btn.classList.toggle('is-active', btn.dataset.kladdeView === view);
  });
  const contentEl = document.getElementById('kladde-page-content');
  if (contentEl) {
    contentEl.innerHTML = renderContent();
    bindContent();
  }
}

// NT-LAYOUT-01 (L5): Die Kladde ist ein Dashboard-Tab (Content-Bereich, App-Shell bleibt
// stehen) statt einer eigenständigen Seite — daher kein eigener Zurück-Button mehr, Navigation
// läuft über Seitenleiste/Bottom-Nav/Browser-Zurück.
let deepLinkPending = false;
let requestedEntry: string | null = null;

/** Wird vom Dashboard beim Wechsel AUF den Kladde-Tab aufgerufen (liest den Deep-Link einmal). */
export function prepareKladdeTab(): void {
  // NT-VIERTEL-07-FEINSCHLIFF (Teil C): Deep-Link aus dem Fragezeichen im Viertel-Tab
  // (`?view=spickzettel&entry=das_viertel`, Muster: login.ts's `?verified=1`) — springt direkt
  // in den Spickzettel und scrollt zum benannten Eintrag (renderSpickzettel() vergibt dafür
  // stabile id-Attribute je hint.key).
  const params = new URLSearchParams(window.location.search);
  currentView = params.get('view') === 'spickzettel' ? 'spickzettel' : 'checkliste';
  requestedEntry = params.get('entry');
  deepLinkPending = currentView === 'spickzettel' && requestedEntry !== null;
  kladdeState = null;
}

export function renderKladdeTab(): string {
  addStyles();
  return `
    <section class="dash-content">
      <div class="kladde-page">
        <h1 class="kladde-title">DIE KLADDE</h1>
        <div class="kladde-view-toggle">
          <button type="button" data-kladde-view="checkliste" class="${currentView === 'checkliste' ? 'is-active' : ''}">CHECKLISTE</button>
          <button type="button" data-kladde-view="spickzettel" class="${currentView === 'spickzettel' ? 'is-active' : ''}">SPICKZETTEL</button>
        </div>
        <div id="kladde-page-content">${renderContent()}</div>
      </div>
    </section>
  `;
}

export async function mountKladdeTab(): Promise<void> {
  document.querySelectorAll<HTMLButtonElement>('[data-kladde-view]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const view = btn.dataset.kladdeView as KladdeView | undefined;
      if (view) setView(view);
    });
  });

  // Dashboard-Polling rendert den Tab regelmäßig neu — nur beim ersten Mount nachladen,
  // danach reicht es, die Listener an das bereits gerenderte Markup zu hängen.
  if (kladdeState === null) await refresh();
  else bindContent();

  if (deepLinkPending && requestedEntry) {
    deepLinkPending = false;
    document.getElementById(`kladde-spickzettel-${requestedEntry}`)?.scrollIntoView({ block: 'start' });
  }
}
